import { html, property } from 'lit-element';
import { KatLitElement, register } from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import wrapperStyles from './panel-wrapper.lit.scss';
import { KatPanel } from './panel';

/**
 * @component {kat-panel-wrapper} KatalPanelWrapper A wrapper for a kat-panel. Controls how the panel interacts with its side-by-side content.
 * @subcomponent ./panel.ts
 * @status Production
 * @slot default Main body content.
 * @slot panel Panel content.
 * @example Panel {"content": "<p>Main body content. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed doeiusmod tempor incididunt ut labore et dolore magna aliqua.</p><kat-panel slot=\"panel\" header=\"Panel header\">Panel Content.</kat-panel>"}
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-panel-wrapper')
export class KatPanelWrapper extends KatLitElement {
  static get styles() {
    return [baseStyles, wrapperStyles];
  }

  /** The visibility of the slotted kat-panel. Can also be controlled via the `visible` property on the panel. Defaults to false. */
  @property({ attribute: 'panel-visible' })
  panelVisible?: boolean = false;

  /** The behavior of how the slotted kat-panel interacts with the content. Defaults to overlay. */
  @property()
  variant?: 'slide' | 'overlay' = 'overlay';

  /** The size of the panel. Defaults to 'small'. */
  @property()
  size?: 'small' | 'large' = 'small';

  constructor() {
    super();

    this._listeners = [
      ['close', this._handlePanelEvent],
      ['open', this._handlePanelEvent],
    ];
  }

  private _handlePanelEvent({ detail }) {
    this.panelVisible = detail.visible;
  }

  // Runs once to handle panel with a visible attribute
  firstUpdated() {
    super.firstUpdated();
    const panel = this.querySelector<KatPanel>('kat-panel');
    if (panel) {
      this.panelVisible = panel.hasAttribute('visible');
    }
  }

  //TODO: Should auto-slot panel
  render() {
    return html`
      <div class="content">
        <slot></slot>
      </div>
      <slot name="panel"></slot>
    `;
  }
}
